import React, { useState, useEffect, useMemo } from "react";
import inventoryAPI, {
  InventoryItem,
  CreateInventoryData,
  UpdateInventoryData,
  AdjustInventoryData,
} from "../../services/inventoryAPI";
import { productAPI } from "../../services/productAPI";
import { branchAPI } from "../../services/branchAPI";
import { salesPointAPI } from "../../services/salesPointAPI";
import warehouseAPI from "../../services/warehouseAPI";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Chip,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  FormControlLabel,
  Switch,
  Tooltip,
  IconButton,
  alpha,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Inventory2 as InventoryIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import TableActionButton from "../../components/layout/TableActionButton";
import PageHeader from "../../components/layout/PageHeader";
import DataListCard from "../../components/layout/DataListCard";
import SortableTableCell from "../../components/layout/SortableTableCell";
import { useRBAC } from "../../hooks/useRBAC";
import { useTableSort } from "../../hooks/useTableSort";
import { useAuthStore } from "../../stores/authStore";
import { useTradeTerminology } from "../../utils/tradeTerminology";
import { tokens } from "../../theme";
import { getApiErrorMessage } from "../../utils/apiError";

type ProductTypeFilter = "all" | "GOODS" | "PREPARED";
type InventorySortField =
  | "product"
  | "branch"
  | "quantity"
  | "reservedQuantity"
  | "availableQuantity"
  | "reorderLevel"
  | "lastRestockedAt"
  | "createdAt";

type InventoryRow = InventoryItem & {
  stockQuantity?: number;
  minStockAlert?: number;
};

const getQuantity = (item: InventoryRow) =>
  Number(item.quantity ?? item.stockQuantity ?? 0);

const getReorderLevel = (item: InventoryRow) =>
  Number(item.reorderLevel ?? item.minStockAlert ?? 0);

const inventoryFormSchema = yup.object().shape({
  productId: yup.string().uuid().required("Product is required"),
  branchId: yup.string().uuid().nullable().optional(),
  salesPointId: yup.string().uuid().nullable().optional(),
  quantity: yup
    .number()
    .typeError("Quantity is required")
    .min(0, "Quantity cannot be negative")
    .required("Initial quantity is required"),
  reorderLevel: yup
    .number()
    .typeError("Reorder level is required")
    .min(0, "Reorder level cannot be negative")
    .required("Reorder level is required"),
});

const adjustInventorySchema = yup.object().shape({
  quantity: yup
    .number()
    .typeError("Quantity is required")
    .positive("Quantity must be greater than 0")
    .required("Adjustment quantity is required"),
  adjustmentType: yup
    .string()
    .oneOf(["add", "remove", "wastage"])
    .required("Adjustment type is required"),
  reason: yup.string().max(500).required("Reason is required"),
  expiryDate: yup.date().nullable().optional(),
});

type InventoryFormData = yup.InferType<typeof inventoryFormSchema>;
type AdjustInventoryFormData = yup.InferType<typeof adjustInventorySchema>;

const InventoryPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { canCreate, canUpdate, canDelete, hasExtraAction, canChangePosScope } =
    useRBAC();
  const canAdjustStock =
    hasExtraAction("inventory", "adjust_stock") ||
    hasExtraAction("product_sales_points", "adjust_stock") ||
    canUpdate("inventory") ||
    canCreate("inventory");
  const { user } = useAuthStore();
  const { terms } = useTradeTerminology();
  const userSalesPointId = user?.salesPointId ?? "";
  const userBranchId = user?.branchId ?? "";
  const { sortBy, sortOrder, handleSort, sortParams } =
    useTableSort<InventorySortField>("product", "asc");
  const onSort = (field: InventorySortField) => handleSort(field, () => setPage(0));
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [branchFilter, setBranchFilter] = useState<string>("all");
  const [salesPointFilter, setSalesPointFilter] = useState<string>("all");
  const [warehouseFilter, setWarehouseFilter] = useState<string>(() => {
    if (typeof window === "undefined") return "all";
    return new URLSearchParams(window.location.search).get("warehouseId") || "all";
  });
  const [productTypeFilter, setProductTypeFilter] = useState<ProductTypeFilter>("all");
  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [openAdjustDialog, setOpenAdjustDialog] = useState(false);
  const [editingInventory, setEditingInventory] =
    useState<InventoryItem | null>(null);
  const [adjustingInventory, setAdjustingInventory] =
    useState<InventoryItem | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    item: InventoryRow | null;
  }>({
    open: false,
    item: null,
  });
  const [alert, setAlert] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const { data: branches = [] } = useQuery({
    queryKey: ["branches", "all"],
    queryFn: () => branchAPI.getAllBranches(),
  });

  const { data: warehousesData } = useQuery({
    queryKey: ["warehouses", "inventory-filter"],
    queryFn: () => warehouseAPI.getWarehouses({ pageSize: 100, isActive: true }),
  });
  const warehousesList = warehousesData?.data || [];

  const listSalesPointId =
    salesPointFilter !== "all"
      ? salesPointFilter
      : canChangePosScope()
        ? ""
        : userSalesPointId;

  const {
    data: inventoryData,
    isLoading,
    error,
  } = useQuery({
    queryKey: [
      "inventory",
      page,
      rowsPerPage,
      debouncedSearch,
      branchFilter,
      salesPointFilter,
      warehouseFilter,
      productTypeFilter,
      lowStockOnly,
      sortBy,
      sortOrder,
      listSalesPointId,
    ],
    queryFn: () =>
      inventoryAPI.getInventory({
        pageSize: rowsPerPage,
        pageNo: page + 1,
        ...sortParams,
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
        ...(branchFilter !== "all" ? { branchId: branchFilter } : {}),
        ...(listSalesPointId ? { salesPointId: listSalesPointId } : {}),
        ...(warehouseFilter !== "all" ? { warehouseId: warehouseFilter } : {}),
        ...(productTypeFilter !== "all" ? { productType: productTypeFilter } : {}),
        ...(lowStockOnly ? { lowStock: true } : {}),
      }),
  });

  const inventoryItems = useMemo(
    () => (inventoryData?.data || []) as InventoryRow[],
    [inventoryData]
  );
  const totalCount = inventoryData?.total || 0;

  const { mutate: createInventory } = useMutation({
    mutationFn: (data: CreateInventoryData) =>
      inventoryAPI.createInventory(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["inventory"] });
      setAlert({ type: "success", message: "Inventory created successfully!" });
      handleCloseDialog();
    },
    onError: (error: unknown) => {
      setAlert({
        type: "error",
        message: getApiErrorMessage(error, "Failed to create inventory"),
      });
    },
  });

  const { mutate: updateInventory } = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateInventoryData }) =>
      inventoryAPI.updateInventory(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["inventory"] });
      setAlert({ type: "success", message: "Inventory updated successfully!" });
      handleCloseDialog();
    },
    onError: (error: unknown) => {
      setAlert({
        type: "error",
        message: getApiErrorMessage(error, "Failed to update inventory"),
      });
    },
  });

  const { mutate: adjustInventory } = useMutation({
    mutationFn: ({ id, data }: { id: string; data: AdjustInventoryData }) =>
      inventoryAPI.adjustInventory(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["inventory"] });
      setAlert({
        type: "success",
        message: "Inventory adjusted successfully!",
      });
      handleCloseAdjustDialog();
    },
    onError: (error: unknown) => {
      setAlert({
        type: "error",
        message: getApiErrorMessage(error, "Failed to adjust inventory"),
      });
    },
  });

  const { mutate: deleteInventory } = useMutation({
    mutationFn: (id: string) => inventoryAPI.deleteInventory(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["inventory"] });
      setAlert({ type: "success", message: "Inventory deleted successfully!" });
      setDeleteDialog({ open: false, item: null });
    },
    onError: (error: unknown) => {
      setAlert({
        type: "error",
        message: getApiErrorMessage(error, "Failed to delete inventory"),
      });
    },
  });

  const {
    control: createControl,
    handleSubmit: handleCreateSubmit,
    reset: resetCreate,
    watch: watchCreate,
    setValue: setCreateValue,
    formState: { errors: createErrors },
  } = useForm<InventoryFormData>({
    resolver: yupResolver(inventoryFormSchema) as never,
    defaultValues: {
      productId: "",
      branchId: userBranchId || "",
      salesPointId: userSalesPointId || "",
      quantity: 0,
      reorderLevel: 0,
    },
  });

  const watchedBranchId = watchCreate("branchId") || "";
  const watchedSalesPointId = watchCreate("salesPointId") || "";

  const { data: formSalesPoints = [] } = useQuery({
    queryKey: ["sales_points", "inventory-form", watchedBranchId],
    queryFn: () =>
      watchedBranchId
        ? salesPointAPI.getSalesPointsByBranch(watchedBranchId)
        : salesPointAPI.getAllSalesPoints(),
    enabled: openDialog && !editingInventory,
  });

  const { data: filterSalesPoints = [] } = useQuery({
    queryKey: [
      "sales_points",
      "inventory-filter",
      branchFilter !== "all" ? branchFilter : "all",
    ],
    queryFn: async () => {
      if (branchFilter !== "all") {
        return salesPointAPI.getSalesPointsByBranch(branchFilter);
      }
      return salesPointAPI.getAllSalesPoints();
    },
    enabled: canChangePosScope(),
  });

  const catalogSalesPointId = editingInventory
    ? ""
    : watchedSalesPointId || userSalesPointId;

  const { data: productsData } = useQuery({
    queryKey: ["products-inventory", catalogSalesPointId],
    queryFn: async () => {
      if (catalogSalesPointId) {
        const deptProducts =
          await productAPI.getProductsBySalesPoint(catalogSalesPointId);
        return deptProducts.map((p: any) => ({
          id: p.productMasterId ?? p.id,
          name: p.name,
          sku: p.sku,
        }));
      }
      return [];
    },
    enabled: !!catalogSalesPointId && openDialog && !editingInventory,
  });

  const productOptions = useMemo(() => {
    const list = Array.isArray(productsData) ? [...productsData] : [];
    if (editingInventory?.productId) {
      const exists = list.some(
        (p: any) => String(p.id) === String(editingInventory.productId)
      );
      if (!exists) {
        list.unshift({
          id: editingInventory.productId,
          name:
            editingInventory.product?.name ||
            (editingInventory as any).productName ||
            "Current product",
          sku: editingInventory.product?.sku || "",
        });
      }
    }
    return list;
  }, [productsData, editingInventory]);

  const {
    control: adjustControl,
    handleSubmit: handleAdjustSubmit,
    reset: resetAdjust,
    formState: { errors: adjustErrors },
  } = useForm<AdjustInventoryFormData>({
    resolver: yupResolver(adjustInventorySchema),
    defaultValues: {
      quantity: 0,
      adjustmentType: "add",
      reason: "",
      expiryDate: null,
    },
  });

  const handleOpenDialog = (inventory?: InventoryItem) => {
    if (inventory) {
      setEditingInventory(inventory);
      resetCreate({
        productId: inventory.productId,
        branchId: inventory.branchId,
        salesPointId: inventory.salesPointId || "",
        quantity: getQuantity(inventory),
        reorderLevel: getReorderLevel(inventory),
      });
    } else {
      setEditingInventory(null);
      resetCreate({
        productId: "",
        branchId: userBranchId || "",
        salesPointId: userSalesPointId || "",
        quantity: 0,
        reorderLevel: 0,
      });
    }
    setOpenDialog(true);
  };

  const handleOpenAdjustDialog = (inventory: InventoryItem) => {
    setAdjustingInventory(inventory);
    resetAdjust({
      quantity: 0,
      adjustmentType: "add",
      reason: "",
      expiryDate: null,
    });
    setOpenAdjustDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingInventory(null);
    resetCreate();
  };

  const handleCloseAdjustDialog = () => {
    setOpenAdjustDialog(false);
    setAdjustingInventory(null);
    resetAdjust();
  };

  const onCreateSubmit = (data: InventoryFormData) => {
    if (editingInventory) {
      updateInventory({
        id: editingInventory.id,
        data: {
          reorderLevel: Number(data.reorderLevel),
          minStockAlert: Number(data.reorderLevel),
        },
      });
      return;
    }
    if (!data.salesPointId) {
      setAlert({ type: "error", message: "Sales point is required" });
      return;
    }
    createInventory({
      productId: data.productId,
      branchId: data.branchId || undefined,
      salesPointId: String(data.salesPointId),
      quantity: Number(data.quantity ?? 0),
      reorderLevel: Number(data.reorderLevel),
      minStockAlert: Number(data.reorderLevel),
    });
  };

  const onAdjustSubmit = (data: AdjustInventoryFormData) => {
    if (!adjustingInventory) return;
    adjustInventory({
      id: adjustingInventory.id,
      data: {
        adjustmentType: data.adjustmentType as AdjustInventoryData["adjustmentType"],
        quantity: Number(data.quantity),
        reason: data.reason,
        expiryDate: data.expiryDate
          ? new Date(data.expiryDate).toISOString()
          : undefined,
      },
    });
  };

  const handleDelete = (item: InventoryRow) => {
    setDeleteDialog({ open: true, item });
  };

  const getStockStatus = (item: InventoryRow) => {
    const qty = getQuantity(item);
    const reorder = getReorderLevel(item) || 1;
    const stockRatio = qty / reorder;
    if (stockRatio <= 0.2)
      return { status: "critical", color: "error", icon: <WarningIcon /> };
    if (stockRatio <= 0.5)
      return { status: "low", color: "warning", icon: <WarningIcon /> };
    return { status: "good", color: "success", icon: <CheckCircleIcon /> };
  };

  const getStockStatusColor = (quantity: number, reorderLevel: number) => {
    if (quantity <= reorderLevel * 0.2) return "error";
    if (quantity <= reorderLevel * 0.5) return "warning";
    return "success";
  };

  const filterDropdowns = (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1.5}
      flexWrap="wrap"
      alignItems={{ sm: "center" }}
      sx={{ width: "100%" }}
    >
      <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 160 } }}>
        <InputLabel>Branch</InputLabel>
        <Select
          value={branchFilter}
          label="Branch"
          onChange={(e) => {
            setBranchFilter(e.target.value);
            setSalesPointFilter("all");
            setPage(0);
          }}
        >
          <MenuItem value="all">All Branches</MenuItem>
          {branches.map((branch: { id: string; name: string }) => (
            <MenuItem key={branch.id} value={branch.id}>
              {branch.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 160 } }}>
        <InputLabel>Warehouse</InputLabel>
        <Select
          value={warehouseFilter}
          label="Warehouse"
          onChange={(e) => {
            setWarehouseFilter(e.target.value);
            setPage(0);
          }}
        >
          <MenuItem value="all">All Warehouses</MenuItem>
          {warehousesList.map((wh: { id: string; name: string; code?: string }) => (
            <MenuItem key={wh.id} value={wh.id}>
              {wh.name} {wh.code ? `(${wh.code})` : ""}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {canChangePosScope() && (
        <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 180 } }}>
          <InputLabel>Sales point</InputLabel>
          <Select
            value={salesPointFilter}
            label="Sales point"
            onChange={(e) => {
              setSalesPointFilter(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="all">All sales points</MenuItem>
            {filterSalesPoints.map((sp: { id: string; name: string }) => (
              <MenuItem key={sp.id} value={sp.id}>
                {sp.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 140 } }}>
        <InputLabel>Type</InputLabel>
        <Select
          value={productTypeFilter}
          label="Type"
          onChange={(e) => {
            setProductTypeFilter(e.target.value as ProductTypeFilter);
            setPage(0);
          }}
        >
          <MenuItem value="all">All Types</MenuItem>
          <MenuItem value="GOODS">Goods</MenuItem>
          <MenuItem value="PREPARED">Prepared</MenuItem>
        </Select>
      </FormControl>
      <FormControlLabel
        control={
          <Switch
            checked={lowStockOnly}
            onChange={(e) => {
              setLowStockOnly(e.target.checked);
              setPage(0);
            }}
            size="small"
          />
        }
        label="Low stock only"
        sx={{ ml: { sm: 0.5 }, mr: 0 }}
      />
    </Stack>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <PageHeader
          title={terms.stockOutletLabel}
          subtitle="Stock by sales-point outlet and inventory locations"
          breadcrumbs={[
            { label: "Dashboard", to: "/dashboard" },
            { label: terms.stockOutletLabel },
          ]}
          action={
            <>
              <Tooltip title="Export PDF">
                <IconButton size="small" sx={{ border: "1px solid", borderColor: "divider" }}>
                  <PdfIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Export Excel">
                <IconButton size="small" sx={{ border: "1px solid", borderColor: "divider" }}>
                  <ExcelIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {canAdjustStock && (
                <Button
                  variant="outlined"
                  color="success"
                  startIcon={<TrendingUpIcon />}
                  onClick={() => {
                    if (inventoryItems.length > 0) {
                      handleOpenAdjustDialog(inventoryItems[0]);
                    } else {
                      setAlert({
                        type: "error",
                        message: "No inventory rows loaded yet. You can adjust stock from each product in the Product List or create stock.",
                      });
                    }
                  }}
                >
                  Adjust Stock
                </Button>
              )}
              {canCreate("inventory") && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenDialog()}
                >
                  Add Inventory
                </Button>
              )}
            </>
          }
        />

        {alert && (
          <Alert severity={alert.type} sx={{ mb: 2 }} onClose={() => setAlert(null)}>
            {alert.message}
          </Alert>
        )}

        {canAdjustStock && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Adjust stock is enabled for your account — use the{" "}
            <strong>Adjust</strong> button on each inventory row.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error instanceof Error ? error.message : "Failed to load inventory"}
          </Alert>
        )}

        <DataListCard
          searchPlaceholder="Search product name or SKU…"
          searchValue={search}
          onSearchChange={(v) => {
            setSearch(v);
            setPage(0);
          }}
          filters={filterDropdowns}
          footer={
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              sx={{ width: "100%" }}
            />
          }
        >
          {isLoading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <Typography color="text.secondary">Loading inventory…</Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small" sx={{ width: "100%" }}>
                <TableHead>
                  <TableRow>
                    <TableCell width={52}>S.No.</TableCell>
                    <SortableTableCell label="Product" field="product" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                    <SortableTableCell label="Branch" field="branch" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                    <TableCell>Status</TableCell>
                    <SortableTableCell label="Qty" field="quantity" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                    <SortableTableCell label="Reserved" field="reservedQuantity" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                    <SortableTableCell label="Available" field="availableQuantity" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                    <SortableTableCell label="Reorder" field="reorderLevel" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                    <SortableTableCell label="Last restocked" field="lastRestockedAt" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {inventoryItems.map((item, index) => {
                    const stockStatus = getStockStatus(item);
                    const qty = getQuantity(item);
                    const reorder = getReorderLevel(item);
                    return (
                      <TableRow key={item.id} hover>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {page * rowsPerPage + index + 1}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Box
                              sx={{
                                width: 28,
                                height: 28,
                                borderRadius: tokens.radius.sm,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: alpha(tokens.colors.dashTeal, 0.12),
                                color: tokens.colors.dashTeal,
                                flexShrink: 0,
                              }}
                            >
                              <InventoryIcon sx={{ fontSize: 16 }} />
                            </Box>
                            <Box minWidth={0}>
                              <Typography variant="body2" fontWeight={600} noWrap>
                                {item.product?.name || "—"}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                SKU: {item.product?.sku || "—"}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600} noWrap>
                            {item.warehouse?.name || item.branch?.name || "—"}
                          </Typography>
                          {item.warehouse && item.branch ? (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Branch: {item.branch.name}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          <Tooltip title={`Stock level: ${stockStatus.status}`}>
                            <Chip
                              icon={stockStatus.icon}
                              label={stockStatus.status.toUpperCase()}
                              color={stockStatus.color as "error" | "warning" | "success"}
                              size="small"
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            color={getStockStatusColor(qty, reorder)}
                          >
                            {qty} {item.product?.unit || item.unit || "PCS"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {item.reservedQuantity ?? 0} {item.product?.unit || item.unit || "PCS"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {item.availableQuantity ?? 0} {item.product?.unit || item.unit || "PCS"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {reorder}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {item.lastRestockedAt
                              ? format(new Date(item.lastRestockedAt), "dd MMM yyyy")
                              : "—"}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            {canAdjustStock && (
                              <Button
                                size="small"
                                variant="outlined"
                                color="success"
                                startIcon={<TrendingUpIcon sx={{ fontSize: 16 }} />}
                                onClick={() => handleOpenAdjustDialog(item)}
                                sx={{ textTransform: "none", whiteSpace: "nowrap" }}
                              >
                                Adjust
                              </Button>
                            )}
                            {canUpdate("inventory") && (
                              <TableActionButton
                                title="Edit reorder level"
                                variant="primary"
                                onClick={() => handleOpenDialog(item)}
                              >
                                <EditIcon sx={{ fontSize: 18 }} />
                              </TableActionButton>
                            )}
                            {canDelete("inventory") && (
                              <TableActionButton
                                title="Delete inventory"
                                variant="danger"
                                onClick={() => handleDelete(item)}
                              >
                                <DeleteIcon sx={{ fontSize: 18 }} />
                              </TableActionButton>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {inventoryItems.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                        <Stack spacing={2} alignItems="center">
                          <Typography color="text.secondary">
                            No inventory found for this outlet
                          </Typography>
                          {canCreate("inventory") && (
                            <Button
                              variant="contained"
                              startIcon={<AddIcon />}
                              onClick={() => handleOpenDialog()}
                            >
                              Add Inventory
                            </Button>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DataListCard>

        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
        >
          <form onSubmit={handleCreateSubmit(onCreateSubmit)}>
            <DialogTitle>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h6" fontWeight={700}>
                  {editingInventory ? "Edit Inventory" : "Add Inventory"}
                </Typography>
                <IconButton size="small" onClick={handleCloseDialog}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Stack>
            </DialogTitle>
            <DialogContent dividers sx={{ maxHeight: "72vh" }}>
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="branchId"
                    control={createControl}
                    render={({ field }) => (
                      <FormControl fullWidth error={!!createErrors.branchId}>
                        <InputLabel>Branch (Optional)</InputLabel>
                        <Select
                          {...field}
                          label="Branch (Optional)"
                          disabled={Boolean(editingInventory)}
                          onChange={(e) => {
                            field.onChange(e);
                            setCreateValue("salesPointId", "");
                            setCreateValue("productId", "");
                          }}
                        >
                          <MenuItem value="">Select Branch (Optional)</MenuItem>
                          {branches.map((branch: { id: string; name: string }) => (
                            <MenuItem key={branch.id} value={branch.id}>
                              {branch.name}
                            </MenuItem>
                          ))}
                        </Select>
                        {createErrors.branchId && (
                          <FormHelperText>
                            {createErrors.branchId.message}
                          </FormHelperText>
                        )}
                      </FormControl>
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="salesPointId"
                    control={createControl}
                    render={({ field }) => (
                      <FormControl
                        fullWidth
                        error={!!(createErrors as { salesPointId?: { message?: string } }).salesPointId}
                      >
                        <InputLabel>Sales point *</InputLabel>
                        <Select
                          {...field}
                          value={field.value || ""}
                          label="Sales point *"
                          disabled={Boolean(editingInventory)}
                          onChange={(e) => {
                            field.onChange(e);
                            setCreateValue("productId", "");
                          }}
                        >
                          <MenuItem value="">Select sales point</MenuItem>
                          {formSalesPoints.map((sp: { id: string; name: string }) => (
                            <MenuItem key={sp.id} value={sp.id}>
                              {sp.name}
                            </MenuItem>
                          ))}
                        </Select>
                        {(createErrors as { salesPointId?: { message?: string } }).salesPointId && (
                          <FormHelperText>
                            {(createErrors as { salesPointId?: { message?: string } }).salesPointId?.message}
                          </FormHelperText>
                        )}
                      </FormControl>
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="productId"
                    control={createControl}
                    render={({ field }) => (
                      <FormControl fullWidth error={!!createErrors.productId}>
                        <InputLabel>Product *</InputLabel>
                        <Select
                          {...field}
                          label="Product *"
                          disabled={
                            Boolean(editingInventory) || !watchedSalesPointId
                          }
                        >
                          <MenuItem value="">Select Product</MenuItem>
                          {productOptions.map((product: { id: string; name: string; sku?: string }) => (
                            <MenuItem key={product.id} value={product.id}>
                              {product.name}
                              {product.sku ? ` (${product.sku})` : ""}
                            </MenuItem>
                          ))}
                        </Select>
                        {createErrors.productId && (
                          <FormHelperText>
                            {createErrors.productId.message}
                          </FormHelperText>
                        )}
                        {!editingInventory &&
                          watchedSalesPointId &&
                          productOptions.length === 0 && (
                            <FormHelperText>
                              No products on this sales point — import/assign products first
                            </FormHelperText>
                          )}
                      </FormControl>
                    )}
                  />
                </Grid>
                {!editingInventory ? (
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="quantity"
                      control={createControl}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          type="number"
                          inputProps={{ min: 0, step: "any" }}
                          label="Opening quantity *"
                          error={!!createErrors.quantity}
                          helperText={
                            createErrors.quantity?.message ||
                            "Creates opening stock via stock adjustment"
                          }
                        />
                      )}
                    />
                  </Grid>
                ) : (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Current quantity"
                      value={getQuantity(editingInventory)}
                      disabled
                      helperText="Use Adjust stock to change quantity"
                    />
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="reorderLevel"
                    control={createControl}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        type="number"
                        inputProps={{ min: 0, step: "any" }}
                        label="Reorder level *"
                        error={!!createErrors.reorderLevel}
                        helperText={createErrors.reorderLevel?.message}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>Cancel</Button>
              <Button type="submit" variant="contained">
                {editingInventory ? "Update" : "Create"}
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        <Dialog
          open={openAdjustDialog}
          onClose={handleCloseAdjustDialog}
          maxWidth="sm"
          fullWidth
        >
          <form onSubmit={handleAdjustSubmit(onAdjustSubmit)}>
            <DialogTitle>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h6" fontWeight={700}>
                  Adjust Inventory — {adjustingInventory?.product?.name}
                </Typography>
                <IconButton size="small" onClick={handleCloseAdjustDialog}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Stack>
            </DialogTitle>
            <DialogContent dividers sx={{ maxHeight: "72vh" }}>
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="adjustmentType"
                    control={adjustControl}
                    render={({ field }) => (
                      <FormControl
                        fullWidth
                        error={!!adjustErrors.adjustmentType}
                      >
                        <InputLabel>Adjustment Type *</InputLabel>
                        <Select {...field} label="Adjustment Type *">
                          <MenuItem value="add">Stock In (Increase)</MenuItem>
                          <MenuItem value="remove">
                            Stock Out (Decrease)
                          </MenuItem>
                          <MenuItem value="wastage">
                            Wastage / kitchen scrap
                          </MenuItem>
                        </Select>
                        {adjustErrors.adjustmentType && (
                          <FormHelperText>
                            {adjustErrors.adjustmentType.message}
                          </FormHelperText>
                        )}
                      </FormControl>
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="quantity"
                    control={adjustControl}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        type="number"
                        inputProps={{ min: 0, step: "any" }}
                        label={`Quantity (${adjustingInventory?.unit || "Unit"}) *`}
                        error={!!adjustErrors.quantity}
                        helperText={adjustErrors.quantity?.message}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="reason"
                    control={adjustControl}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Reason *"
                        multiline
                        rows={2}
                        placeholder="e.g. Expired, spill, kitchen scrap"
                        error={!!adjustErrors.reason}
                        helperText={adjustErrors.reason?.message}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseAdjustDialog}>Cancel</Button>
              <Button type="submit" variant="contained">
                Adjust Stock
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        <Dialog
          open={deleteDialog.open}
          onClose={() => setDeleteDialog({ open: false, item: null })}
        >
          <DialogTitle sx={{ fontWeight: 700 }}>Delete inventory</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete inventory for{" "}
              <strong>{deleteDialog.item?.product?.name}</strong> at{" "}
              <strong>{deleteDialog.item?.branch?.name}</strong>? This cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setDeleteDialog({ open: false, item: null })}
              color="inherit"
            >
              Cancel
            </Button>
            <Button
              onClick={() =>
                deleteDialog.item && deleteInventory(deleteDialog.item.id)
              }
              color="error"
              variant="contained"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default InventoryPage;
